import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X } from "lucide-react";

/**
 * Reusable searchable select / combobox.
 * - options: Array<{ value: string, label: string, hint?: string }>
 * - value: currently selected value
 * - onChange: (value: string) => void
 * - placeholder, testid, disabled, clearable
 */
export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "— wählen —",
  testid,
  disabled = false,
  clearable = true,
  className = "",
  emptyText = "Keine Treffer",
  footer = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        (o.hint || "").toLowerCase().includes(q) ||
        (o.value || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const baseInput =
    "w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-sm text-zinc-100 focus:ring-2 focus:ring-blue-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed";

  function pick(v) {
    onChange?.(v);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`${baseInput} flex items-center justify-between gap-2 text-left`}
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span className={`truncate ${selected ? "text-zinc-100" : "text-zinc-500"}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 text-zinc-500">
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); pick(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); pick(""); } }}
              className="hover:text-zinc-200 p-0.5 -m-0.5 rounded"
              data-testid={testid ? `${testid}-clear` : undefined}
              aria-label="Auswahl löschen"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-900 dark:bg-zinc-900 text-zinc-100 border border-zinc-700 rounded shadow-2xl"
             style={{ backgroundColor: "rgb(24 24 27)", colorScheme: "dark" }}>
          <div className="p-2 border-b border-zinc-800 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-zinc-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suchen…"
              className="bg-transparent text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none flex-1"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-zinc-400 italic">{emptyText}</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o.value)}
                  data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 transition-colors ${
                    o.value === value
                      ? "bg-blue-600/30 text-blue-100"
                      : "text-zinc-100 hover:bg-zinc-800"
                  }`}
                >
                  <span className="truncate">{o.label}</span>
                  {o.hint && <span className="text-xs text-zinc-400 font-mono shrink-0">{o.hint}</span>}
                </button>
              ))
            )}
          </div>
          {footer ? (
            <div className="border-t border-zinc-800 px-2 py-1.5 bg-zinc-900/60">{footer}</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
